import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate, useLocation as useRouterLocation } from "react-router-dom";
import { toast } from "sonner";
import {
  Check,
  FileText,
  Circle,
  Camera,
  ClipboardList,
  Phone,
  Calendar,
  ChevronRight,
  ChevronLeft,
  AlertCircle,
  Stethoscope,
  Headphones,
  ArrowLeft,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardHeader,
  CardContent,
  CardFooter,
  CardTitle,
} from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import AuthService from "@/lib/auth";
import { convertBlobToWav } from "@/utils/audioConverter";
import { useLocation } from '@/contexts/LocationContext';

import { cn, formatPhoneDisplay } from "@/lib/utils.ts";

import biometricAuthService from "@/services/biometricAuthService";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Mic, StopCircle } from "lucide-react";

interface Patient {
  id: string;
  first_name: string;
  middle_name?: string | null;
  last_name: string;
  mobile_number: string;
  date_of_birth: string;
  gender: string;
  email: string;
  hipaa_email_and_sms_consent?: boolean;
  hipaa_email_and_sms_consent_date?: string;
  selected_tests?: string[] | null;
  consent_logs?: any[] | null;
}

interface Step {
  id?: string;
  title: string;
  icon: React.ReactNode;
  isTestStep?: boolean;
  isSmallStep?: boolean;
}

interface StepperProps {
  currentStep: number;
  totalSteps: number;
  steps: Step[];
}

const Stepper: React.FC<StepperProps> = ({
  currentStep,
  totalSteps,
  steps,
}) => {
  return (
    <div className="w-full mb-4 md:mb-8 px-2">
      <div className="flex items-center justify-between relative">
        {/* Progress Line */}
        <div className="absolute top-3 md:top-5 left-0 w-full h-0.5 bg-gray-200 z-0">
          <div
            className="h-full bg-gradient-to-r from-green-400 to-emerald-500 transition-all duration-500"
            style={{
              width: `${((currentStep - 1) / (totalSteps - 1)) * 100}%`,
            }}
          />
        </div>

        {/* Steps */}
        {steps.map((step, index) => {
          const stepNumber = index + 1;
          const isCompleted = stepNumber < currentStep;
          const isCurrent = stepNumber === currentStep;
          const isUpcoming = stepNumber > currentStep;

          return (
            <div
              key={index}
              className="flex flex-col items-center relative z-10"
            >
              <div
                className={`
                w-8 h-8 md:w-10 md:h-10 rounded-full flex items-center justify-center border-2 transition-all duration-300
                ${isCompleted ? "bg-green-500 border-green-500 text-white" : ""}
                ${isCurrent ? "bg-emerald-600 border-emerald-600 text-white shadow-lg" : ""}
                ${isUpcoming ? "bg-white border-gray-300 text-gray-400" : ""}
              `}
              >
                {isCompleted ? (
                  <Check className="w-3 h-3 md:w-4 md:h-4" />
                ) : (
                  <div className="w-3 h-3 md:w-4 md:h-4 flex items-center justify-center">
                    {step.icon}
                  </div>
                )}
              </div>
              <div
                className={`
                mt-1 text-[10px] md:text-xs font-medium text-center max-w-16 md:max-w-20 overflow-hidden text-ellipsis whitespace-nowrap
                ${isCurrent ? "text-emerald-600 font-semibold" : ""}
                ${isCompleted ? "text-green-600" : ""}
                ${isUpcoming ? "text-gray-400" : ""}
              `}
              >
                {step.title}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const Header: React.FC = () => {
  return (
    <header className="w-full flex items-center justify-between px-3 sm:px-6 py-2 sm:py-3 bg-white shadow-sm border-b sticky top-0 z-50">
      <div className="flex items-center space-x-2 sm:space-x-4">
        <span className="text-lg sm:text-xl font-bold text-primary tracking-tight whitespace-nowrap">
          <span className="hidden sm:inline">Monitor Health AI</span>
          <span className="sm:hidden">Monitor Health AI</span>
        </span>
      </div>
      <div className="flex items-center space-x-2 sm:space-x-4">
        {/* Notification Icon - Hidden on mobile to save space */}
        <button className="relative hidden sm:block">
          <span className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full"></span>
          <svg
            width="20"
            height="20"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            viewBox="0 0 24 24"
            className="w-5 h-5"
          >
            <path d="M15 17h5l-1.405-1.405A2.032 2.032 0 0 1 18 14.158V11a6.002 6.002 0 0 0-4-5.659V4a2 2 0 1 0-4 0v1.341C7.67 7.165 6.998 8.388 7 10v4.159c0 .538-.214 1.055-.595 1.436L5 17h5m5 0v1a3 3 0 1 1-6 0v-1m6 0H9" />
          </svg>
        </button>
        {/* Avatar/Logo */}
        <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-primary flex items-center justify-center text-white font-bold text-base sm:text-lg shadow">
          <span className="text-sm sm:text-base">MH</span>
        </div>
      </div>
    </header>
  );
};

// Step 1: HIPAA Consent Component
const HIPAAConsentStep: React.FC<{
  patientId: string;
  patientName: string;
  onNext: () => void;
}> = ({ patientId, patientName, onNext }) => {
  const [loading, setLoading] = useState(false);
  const [consents, setConsents] = useState({
    hipaa: false,
    email: false,
    biometric: false,
    e_sign: false,
  });

  const handleConsentChange = (name: keyof typeof consents) => {
    setConsents((prev) => ({
      ...prev,
      [name]: !prev[name],
    }));
  };

  const isFormValid = () => {
    return consents.hipaa && consents.email && consents.e_sign;
  };

  const handleHipaaConsent = async (accepted: boolean) => {
    if (!accepted) {
      toast.error("You must accept the HIPAA consent to proceed");
      return;
    }

    if (!isFormValid()) {
      toast.error("Please review and accept all required consents");
      return;
    }

    setLoading(true);
    const toastId = toast.loading("Saving your consent preferences...");

    try {
      const accessCode = new URLSearchParams(window.location.search).get('access_code');
      const existingPatient = (accessCode
        ? await AuthService.getPublicPatient(patientId, accessCode)
        : await AuthService.getPatient(patientId)
      ) as { consent_logs?: any[]; first_name?: string; last_name?: string } | null;

      // Create new consent entries with consistent timestamps
      const timestamp = new Date().toISOString();
      const patientName = existingPatient
        ? `${existingPatient.first_name || ""} ${existingPatient.last_name || ""}`.trim()
        : "Patient";

      const newConsentEntries = [
        {
          type: "hipaa_consent",
          status: accepted ? "accepted" : "declined",
          details: `${patientName} accepted HIPAA consent form`,
          timestamp,
          recorded_by: "patient",
          version: "1.0",
          metadata: { ip: "" }, // Could be populated with additional context
        },
        {
          type: "email_consent",
          status: consents.email ? "accepted" : "declined",
          details: consents.email
            ? `${patientName} consented to email communications`
            : `${patientName} declined email communications`,
          timestamp,
          recorded_by: "patient",
          version: "1.0",
          metadata: {},
        },
        {
          type: "sms_consent",
          status: "accepted",
          details: `${patientName} consented to receive SMS notifications`,
          timestamp,
          recorded_by: "patient",
          version: "1.0",
          metadata: {},
        },
        {
          type: "e_sign_consent",
          status: consents.e_sign ? "accepted" : "declined",
          details: consents.e_sign
            ? `${patientName} consented to e-sign communications`
            : `${patientName} declined e-sign communications`,
          timestamp,
          recorded_by: "patient",
          version: "1.0",
          metadata: {},
        },
        {
          type: "biometric_consent",
          status: consents.biometric ? "accepted" : "declined",
          details: consents.biometric
            ? `${patientName} consented to biometric data processing`
            : `${patientName} declined biometric data processing`,
          timestamp,
          recorded_by: "patient",
          version: "1.0",
          metadata: {},
        },
      ];

      // Merge with existing logs, ensuring no duplicates by type if needed
      const existingLogs = Array.isArray(existingPatient?.consent_logs)
        ? existingPatient.consent_logs.filter(Boolean)
        : [];

      const isSMSFlow = !!new URLSearchParams(window.location.search).get('access_code');
      const accessCodeForConsent = new URLSearchParams(window.location.search).get('access_code');

      if (isSMSFlow && accessCodeForConsent) {
        // SMS flow: use the public consent endpoint (no JWT required).
        // This saves the consent entries to the DB for full audit compliance.
        await AuthService.savePublicConsent(patientId, accessCodeForConsent, newConsentEntries);
      } else {
        // Authenticated app flow: use the standard JWT-protected updatePatient.
        await AuthService.updatePatient(patientId, {
          consent_logs: [...existingLogs, ...newConsentEntries],
        });
      }

      toast.success("Your consent preferences have been saved successfully!", {
        id: toastId,
      });
      onNext();
    } catch (error) {
      console.error("Error saving consent:", error);
      toast.error(
        "We couldn't save your consent preferences. Please try again or contact support if the issue persists.",
        { id: toastId },
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="rounded-2xl shadow-lg shadow-green-200/40 border border-gray-200">
      <CardHeader className="border-b px-6 pt-6 pb-4">
        <div className="flex items-center">
          <FileText className="mr-3 text-green-600" size={24} />
          <h2 className="text-xl sm:text-2xl font-bold text-gray-800">
            HIPAA Privacy Notice
          </h2>
        </div>
        <p className="text-gray-600 mt-2">
          In case this form is being filled on behalf of patient, please ask the
          patients for consent
        </p>
      </CardHeader>

      <CardContent className="p-6 max-h-96 overflow-y-auto text-sm text-gray-700 space-y-4">
        <p>
          <strong>
            This notice describes how medical information about you may be used
            and disclosed and how you can get access to this information. Please
            review it carefully.
          </strong>
        </p>

        <div>
          <h4 className="font-semibold mb-1">
            Uses and Disclosures of Protected Health Information
          </h4>
          <p>
            We may use and disclose your protected health information (PHI) for
            the following purposes:
          </p>
          <ul className="list-disc list-inside ml-4 space-y-1">
            <li>
              <strong>Treatment:</strong> Coordinating and managing your
              healthcare.
            </li>
            <li>
              <strong>Payment:</strong> To receive payment for services.
            </li>
            <li>
              <strong>Healthcare Operations:</strong> For internal healthcare
              management.
            </li>
          </ul>
        </div>

        <div>
          <h4 className="font-semibold mb-1">Your Rights Regarding Your PHI</h4>
          <ul className="list-disc list-inside ml-4 space-y-1">
            <li>Request restrictions on disclosures</li>
            <li>Receive confidential communications</li>
            <li>Inspect and copy your PHI</li>
            <li>Request amendments</li>
            <li>Get an accounting of disclosures</li>
            <li>Request a paper copy of this notice</li>
          </ul>
        </div>

        <div>
          <h4 className="font-semibold mb-1">Complaints</h4>
          <p>
            If you believe your privacy rights have been violated, file a
            complaint with us or the Department of Health and Human Services.
          </p>
        </div>

        <p className="text-xs text-gray-500 mt-2">
          Effective as of January 1, 2024.
        </p>
      </CardContent>

      <CardFooter className="p-6 border-t">
        <div className="space-y-4 w-full">
          <div className="flex items-start space-x-3">
            <Checkbox
              id="hipaa-consent"
              checked={consents.hipaa}
              className="border-2 border-green-500 rounded hover:border-green-600 focus:ring-2 focus:ring-green-200 focus:ring-offset-2"
              onCheckedChange={() => handleConsentChange("hipaa")}
            />
            <div className="grid gap-1.5 leading-none">
              <Label
                htmlFor="hipaa-consent"
                className="text-sm font-medium leading-none"
              >
                I <span className="font-semibold">{patientName}</span> have read and accept the HIPAA Privacy Notice above
              </Label>
              <p className="text-sm text-muted-foreground">
                I understand how my protected health information may be used and disclosed.
              </p>
            </div>
          </div>

          <div className="flex items-start space-x-3">
            <Checkbox
              id="email-consent"
              checked={consents.email}
              className="border-2 border-green-500 rounded hover:border-green-600 focus:ring-2 focus:ring-green-200 focus:ring-offset-2"
              onCheckedChange={() => handleConsentChange("email")}
            />
            <div className="grid gap-1.5 leading-none">
              <Label
                htmlFor="email-consent"
                className="text-sm font-medium leading-none"
              >
                I <span className="font-semibold">{patientName}</span> consent
                to receive email & SMS communications in accordance with our <a href="http://monitorhealth.ai/" target="_blank" rel="noopener noreferrer" className="text-green-600 hover:underline">Privacy Terms and Conditions</a>
              </Label>
              <p className="text-sm text-muted-foreground">
                We'll send you important updates and notifications about your
                account.
              </p>
            </div>
          </div>

          <div className="flex items-start space-x-3">
            <Checkbox
              id="e-sign-consent"
              checked={consents.e_sign}
              className="border-2 border-green-500 rounded hover:border-green-600 focus:ring-2 focus:ring-green-200 focus:ring-offset-2"
              onCheckedChange={() => handleConsentChange("e_sign")}
            />
            <div className="grid gap-1.5 leading-none">
              <Label
                htmlFor="e-sign-consent"
                className="text-sm font-medium leading-none"
              >
                I <span className="font-semibold">{patientName}</span> consent
                to e-sign
              </Label>
              <p className="text-sm text-muted-foreground">
                I agree to use electronic signatures in place of written
                signatures for all documents.
              </p>
            </div>
          </div>

          <div className="flex items-start space-x-3">
            <Checkbox
              id="biometric-consent"
              checked={consents.biometric}
              className="border-2 border-green-500 rounded hover:border-green-600 focus:ring-2 focus:ring-green-200 focus:ring-offset-2"
              onCheckedChange={() => handleConsentChange("biometric")}
            />
            <div className="grid gap-1.5 leading-none">
              <Label
                htmlFor="biometric-consent"
                className="text-sm font-medium leading-none"
              >
                I <span className="font-semibold">{patientName}</span> consent
                to facial recognition for identity verification <span className="text-xs text-muted-foreground font-normal">(optional)</span>
              </Label>
              <p className="text-sm text-muted-foreground">
                Your facial biometric data will be processed by AWS Rekognition.
                No images or videos will be stored.
              </p>
            </div>
          </div>

          <div className="flex flex-col gap-4 pt-4">
            <Button
              onClick={() => handleHipaaConsent(true)}
              disabled={!isFormValid() || loading}
              className="w-full text-white font-semibold h-[56px] text-base rounded-full bg-green-500 hover:bg-green-600 hover:scale-[1.02] transition-transform shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <div className="h-5 w-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  <Check className="mr-2" size={18} />
                  Accept & Continue
                </>
              )}
            </Button>
          </div>
        </div>
      </CardFooter>
    </Card>
  );
};

// Step 2: Biometric Verification Component
const BiometricVerificationStep: React.FC<{
  patientId: string;
  onNext: () => void;
  onBack: () => void;
}> = ({ patientId, onNext, onBack }) => {
  const [showCameraModal, setShowCameraModal] = useState(false);
  const [cameraStream, setCameraStream] = useState<MediaStream | null>(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState<string>("");
  const [recordingTimer, setRecordingTimer] = useState(0);
  const videoRef = useRef<HTMLVideoElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const recordedChunksRef = useRef<Blob[]>([]);

  const stopCamera = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach((track) => track.stop());
      setCameraStream(null);
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state !== "inactive"
    ) {
      mediaRecorderRef.current.stop();
    }
  };

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: "user",
          width: { ideal: 640 },
          height: { ideal: 480 },
        },
        audio: false, // We don't need audio for face verification
      });
      setCameraStream(stream);
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (err) {
      toast.error("Failed to access camera: " + err);
    }
  };

  const startRecording = () => {
    if (!cameraStream) return;

    recordedChunksRef.current = [];
    setIsRecording(true);
    setRecordingTimer(0);
    setVerificationStatus("Recording video for verification...");

    // Use the same MIME type logic that was working before
    let options: MediaRecorderOptions = {};

    if (MediaRecorder.isTypeSupported("video/webm;codecs=vp8")) {
      options = {
        mimeType: "video/webm;codecs=vp8",
        videoBitsPerSecond: 400_000,
      };
    } else if (MediaRecorder.isTypeSupported("video/webm;codecs=vp9")) {
      options = {
        mimeType: "video/webm;codecs=vp9",
        videoBitsPerSecond: 400_000,
      };
    } else if (MediaRecorder.isTypeSupported("video/webm")) {
      options = { mimeType: "video/webm", videoBitsPerSecond: 400_000 };
    } else if (MediaRecorder.isTypeSupported("video/mp4")) {
      options = { mimeType: "video/mp4", videoBitsPerSecond: 400_000 };
    } else {
      // fallback: let browser pick
      options = { videoBitsPerSecond: 400_000 };
    }

    let mediaRecorder: MediaRecorder;
    try {
      mediaRecorder = new MediaRecorder(cameraStream, options);
    } catch (err) {
      toast.error("Video recording is not supported in this browser: " + err);
      setIsRecording(false);
      setVerificationStatus("");
      return;
    }

    mediaRecorderRef.current = mediaRecorder;

    mediaRecorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        recordedChunksRef.current.push(event.data);
      }
    };

    mediaRecorder.onstop = async () => {
      const blob = new Blob(recordedChunksRef.current, {
        type: mediaRecorder.mimeType,
      });
      const fileExt = mediaRecorder.mimeType.includes("mp4") ? "mp4" : "webm";
      const videoFile = new File(
        [blob],
        `verification-${Date.now()}.${fileExt}`,
        { type: mediaRecorder.mimeType },
      );

      console.log(
        "Recorded video file size:",
        videoFile.size,
        "bytes (",
        (videoFile.size / 1024 / 1024).toFixed(2),
        "MB )",
      );

      await submitVideoForVerification(videoFile);
    };

    mediaRecorder.start();

    // Timer for 3 seconds
    const timer = setInterval(() => {
      setRecordingTimer((prev) => {
        if (prev >= 2) {
          // 0, 1, 2 = 3 seconds
          clearInterval(timer);
          if (
            mediaRecorderRef.current &&
            mediaRecorderRef.current.state === "recording"
          ) {
            mediaRecorderRef.current.stop();
          }
          setIsRecording(false);
          return 0;
        }
        return prev + 1;
      });
    }, 1000);
  };

  const submitVideoForVerification = async (videoFile: File) => {
    setIsVerifying(true);
    setVerificationStatus("Verifying face...");

    try {
      setIsVerifying(true);
      setVerificationStatus("Verifying face...");

      ///Login here 

      // Note: Even though it's a video, the API expects it as 'image' parameter
      const result = await biometricAuthService.biometricLogin(videoFile, patientId);

      // Check if the API call was successful first
      if (!result.success) {
        // Handle API service layer errors
        const errorMessage =
          result.error?.message || "Biometric verification failed";
        toast.error(errorMessage);
        setVerificationStatus(errorMessage);
        return;
      }

      // Now handle the actual API response - access data property
      const apiResponse = result.data;

      if (apiResponse?.status === "success" && apiResponse.user) {
        // Ensure user is not a boolean (fixing the interface issue)
        if (typeof apiResponse.user === "object" && apiResponse.user.uuid) {
          if (apiResponse.user.uuid === patientId) {
            toast.success(
              `Face verification successful! Welcome ${apiResponse.user.first_name} ${apiResponse.user.last_name}`,
            );
            setShowCameraModal(false);
            setVerificationStatus("Verification successful");
            onNext();
          } else {
            toast.error("Face does not match patient records.");
            setVerificationStatus("Verification failed - face mismatch");
          }
        } else {
          toast.error("Invalid user data received from server.");
          setVerificationStatus("Verification failed - invalid response");
        }
      } else if (apiResponse?.status === "failed") {
        toast.error(
          apiResponse.message || "No face found or verification failed.",
        );
        setVerificationStatus("No face detected");
      } else {
        toast.error(apiResponse?.message || "Biometric verification failed.");
        setVerificationStatus("Verification failed");
      }
    } catch (error) {
      console.error("Biometric verification error:", error);

      // Handle different types of errors
      if (error instanceof Error) {
        toast.error(`Verification failed: ${error.message}`);
      } else if (typeof error === "string") {
        toast.error(`Verification failed: ${error}`);
      } else {
        toast.error("Verification failed: An unexpected error occurred");
      }

      setVerificationStatus("Verification error");
    } finally {
      setIsVerifying(false);
    }
  };

  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsVerifying(true);
    setVerificationStatus("Verifying uploaded file...");

    try {
      setIsVerifying(true);
      setVerificationStatus("Verifying face...");

      // Note: Even though it's a video, the API expects it as 'image' parameter
      const result = await biometricAuthService.biometricLogin(file, patientId);

      // Check if the API call was successful first
      if (!result.success) {
        // Handle API service layer errors
        const errorMessage =
          result.error?.message || "Biometric verification failed";
        toast.error(errorMessage);
        setVerificationStatus("Verification failed - No Face Found");
        return;
      }

      // Now handle the actual API response
      const apiResponse = result.data;

      if (apiResponse?.status === "success" && apiResponse.user) {
        // Ensure user is not a boolean (fixing the interface issue)
        if (typeof apiResponse.user === "object" && apiResponse.user.uuid) {
          if (apiResponse.user.uuid === patientId) {
            toast.success(
              `Face verification successful! Welcome ${apiResponse.user.first_name} ${apiResponse.user.last_name}`,
            );
            setShowCameraModal(false);
            setVerificationStatus("Verification successful");
            onNext();
          } else {
            toast.error("Face does not match patient records.");
            setVerificationStatus("Verification failed - face mismatch");
          }
        } else {
          toast.error("Invalid user data received from server.");
          setVerificationStatus("Verification failed - invalid response");
        }
      } else if (apiResponse?.status === "failed") {
        toast.error(
          apiResponse.message || "No face found or verification failed.",
        );
        setVerificationStatus("No face detected");
      } else {
        toast.error(apiResponse?.message || "Biometric verification failed.");
        setVerificationStatus("Verification failed");
      }
    } catch (error) {
      console.error("Biometric verification error:", error);

      // Handle different types of errors
      if (error instanceof Error) {
        toast.error(`Verification failed: ${error.message}`);
      } else if (typeof error === "string") {
        toast.error(`Verification failed: ${error}`);
      } else {
        toast.error("Verification failed: An unexpected error occurred");
      }

      setVerificationStatus("Verification error");
    } finally {
      setIsVerifying(false);
    }
  };

  useEffect(() => {
    if (showCameraModal) {
      startCamera();
    } else {
      stopCamera();
    }
    return () => stopCamera();
  }, [showCameraModal]);

  return (
    <Card className="rounded-2xl shadow-lg shadow-blue-200/40 border border-gray-200">
      <CardHeader className="border-b px-6 pt-6 pb-4">
        <div className="flex items-center">
          <Camera className="mr-3 text-blue-600" size={24} />
          <h2 className="text-xl sm:text-2xl font-bold text-gray-800">
            Biometric Verification
          </h2>
        </div>
        <p className="text-gray-600 mt-2">
          Please verify your identity using facial recognition
        </p>
      </CardHeader>

      <CardContent className="p-6">
        <div className="text-center space-y-6">
          <div className="w-24 h-24 mx-auto bg-blue-100 rounded-full flex items-center justify-center">
            <Camera className="w-12 h-12 text-blue-600" />
          </div>

          <div>
            <h3 className="text-lg font-semibold text-gray-800 mb-2">
              Identity Verification Required
            </h3>
            <p className="text-gray-600">
              To proceed with the questionnaire, we need to verify your identity
              using facial recognition. We'll record a 3-second video for
              verification.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
            <Button
              onClick={() => setShowCameraModal(true)}
              disabled={isVerifying}
              className="flex-1 h-12 bg-blue-600 hover:bg-blue-700 text-white rounded-xl"
            >
              <Camera className="mr-2 w-5 h-5" />
              Record Video
            </Button>

            <Button
              onClick={() => fileInputRef.current?.click()}
              disabled={isVerifying}
              variant="outline"
              className="flex-1 h-12 border-blue-200 text-blue-700 hover:bg-blue-50 rounded-xl"
            >
              <svg
                className="mr-2 w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                />
              </svg>
              Upload File
            </Button>
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,video/*"
            onChange={handleFileUpload}
            className="hidden"
          />

          {(isVerifying || verificationStatus) && (
            <div className="flex items-center justify-center gap-2 text-blue-600">
              <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
              {verificationStatus || "Verifying identity..."}
            </div>
          )}
        </div>
      </CardContent>

      <CardFooter className="p-6 border-t">
        <div className="flex gap-4 w-full">
          <Button
            onClick={onBack}
            variant="outline"
            className="flex-1 h-12 rounded-xl"
          >
            <ChevronLeft className="mr-2 w-4 h-4" />
            Back
          </Button>
          <Button
            onClick={onNext}
            className="flex-1 h-12 bg-gray-400 text-white rounded-xl cursor-not-allowed"
          //  disabled
          >
            Skip Verification
            <ChevronRight className="ml-2 w-4 h-4" />
          </Button>
        </div>
      </CardFooter>

      {/* Camera Modal */}
      <Dialog open={showCameraModal} onOpenChange={setShowCameraModal}>
        <DialogContent className="max-w-2xl rounded-3xl">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-blue-800">
              Face Verification
            </DialogTitle>
            <DialogDescription className="text-gray-600">
              Position your face in the camera and click record. We'll capture a
              3-second video for verification.
            </DialogDescription>
          </DialogHeader>

          {verificationStatus && (
            <div className="mb-4 text-blue-700 bg-blue-50 px-4 py-3 rounded-xl border border-blue-200">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                {verificationStatus}
              </div>
            </div>
          )}

          {isRecording && (
            <div className="mb-4 text-red-700 bg-red-50 px-4 py-3 rounded-xl border border-red-200">
              <div className="flex items-center justify-center gap-2">
                <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
                <span className="font-semibold">
                  Scanning face: {recordingTimer + 1}/3 seconds
                </span>
              </div>
            </div>
          )}

          <div className="relative bg-gray-900 rounded-2xl overflow-hidden">
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className="w-full h-auto max-h-[60vh]"
            />
            <div className="absolute bottom-6 left-0 right-0 flex justify-center">
              <Button
                onClick={startRecording}
                disabled={isVerifying || isRecording}
                className="bg-blue-600 hover:bg-blue-700 text-white h-12 px-6 rounded-xl shadow-lg"
              >
                {isRecording ? (
                  <>
                    <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse mr-2" />
                    Recording...
                  </>
                ) : isVerifying ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    <Camera className="mr-2 w-5 h-5" />
                    Start Recording
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

// Step 3: Questionnaire Component (Placeholder)
// Step 3: Questionnaire Component with Comment Modal
const QuestionnaireStep: React.FC<{
  patientId: string;
  onComplete: () => void;
}> = ({ patientId, onComplete }) => {
  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState<
    "selection" | "questionnaire" | "review" | "complete"
  >("selection");
  const [availableTests, setAvailableTests] = useState<any[]>([]);
  const [testsProgress, setTestsProgress] = useState<any[]>([]);
  const [currentTestIndex, setCurrentTestIndex] = useState(0);
  const [allTestsCompleted, setAllTestsCompleted] = useState(false);
  const [isLoadingTests, setIsLoadingTests] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [patientName, setPatientName] = useState<string>("");

  const [commentText, setCommentText] = useState("");
  const [currentQuestionForComment, setCurrentQuestionForComment] = useState<{
    id: string;
    question: string;
    selectedValue: number;
    options: { value: number; label: string }[];
  } | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessingAudio, setIsProcessingAudio] = useState(false);
  const [recordingTimeLeft, setRecordingTimeLeft] = useState(0);
  const [transcription, setTranscription] = useState<{
    text: string;
    segments?: Array<{ start: number; end: number; text: string }>;
    language?: string;
    originalLanguage?: string;
    originalText?: string;
    audioFileUrl?: string;
    s3Key?: string;
  } | null>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const recordingTimerRef = useRef<NodeJS.Timeout | null>(null);
  const countdownTimerRef = useRef<NodeJS.Timeout | null>(null);
  const { selectedLocationId, locations } = useLocation();

  // Load patient's selected tests
  // Fixed version of the loadPatientTests function to prevent duplicates
  useEffect(() => {
    const loadPatientTests = async () => {
      if (!patientId) {
        setError("Patient ID is required");
        return;
      }

      setIsLoadingTests(true);
      setError(null);

      try {
        // Enforce authorization: require either a valid SMS access code or an authenticated clinical session
        const accessCode = new URLSearchParams(window.location.search).get('access_code');
        const hasAuthToken = !!localStorage.getItem('jwt_token');

        if (!accessCode && !hasAuthToken) {
          throw new Error("Access code is required to view this questionnaire.");
        }

        const patient = (accessCode
          ? await AuthService.getPublicPatient(patientId, accessCode)
          : await AuthService.getPatient(patientId)
        ) as { first_name?: string; last_name?: string; selected_tests?: any } | null;
        if (!patient) {
          throw new Error("Patient not found or invalid access credentials");
        }

        // Set patient name
        const fullName =
          `${patient.first_name || ""} ${patient.last_name || ""}`.trim();
        setPatientName(fullName || "Patient");

        // Parse selected tests and remove duplicates
        let selectedTestIds: string[] = [];
        if (patient.selected_tests) {
          try {
            const rawTestIds = Array.isArray(patient.selected_tests)
              ? patient.selected_tests
              : JSON.parse(patient.selected_tests);

            // Remove duplicates using Set

            selectedTestIds = Array.from(new Set(rawTestIds)).filter(
              (test): test is string => typeof test === "string",
            );
          } catch (e) {
            console.error("Error parsing selected tests:", e);
            throw new Error("Error parsing assigned tests");
          }
        }

        if (selectedTestIds.length === 0) {
          throw new Error("No tests assigned to this patient");
        }

        console.log("Selected test IDs (deduplicated):", selectedTestIds);

        const templates = await AuthService.getQuestionnaireTemplates({ ids: selectedTestIds });
        if (!templates || templates.length === 0) {
          throw new Error("Error loading questionnaire templates");
        }

        console.log("Fetched templates:", templates);

        // Process templates with improved name generation
        const processedTemplates = templates
          .map((template) => {
            let questionsData: any[] = [];

            if (template.questions) {
              try {
                questionsData = Array.isArray(template.questions)
                  ? template.questions
                  : JSON.parse(template.questions as unknown as string);
              } catch (e) {
                console.error(
                  `Error parsing questions for template ${template.id}:`,
                  e,
                );
                questionsData = [];
              }
            }

            const processedQuestions = questionsData.map(
              (q: any, qIndex: number) => ({
                ...q,
                id: q.id || `question_${qIndex}`,
                selected: null,
                comment: "",
                options: q.options.map((opt: any, idx: number) => ({
                  ...opt,
                  value: opt.value ?? idx,
                  label: opt.label || opt.text || `Option ${idx + 1}`,
                })),
              }),
            );

            // Improved name generation with more specific mapping
            const generateNameFromId = (id: string): string => {
              const lowerId = id.toLowerCase();

              // More specific pattern matching to avoid duplicates
              if (lowerId === "phq-9" || lowerId === "phq9")
                return "PHQ-9 Depression Screening";
              if (lowerId === "gad-7" || lowerId === "gad7")
                return "GAD-7 Anxiety Screening";
              if (lowerId.includes("phq") && lowerId.includes("9"))
                return "PHQ-9 Depression Screening";
              if (lowerId.includes("gad") && lowerId.includes("7"))
                return "GAD-7 Anxiety Screening";
              if (lowerId.includes("depression") && !lowerId.includes("phq"))
                return "Depression Assessment";
              if (lowerId.includes("anxiety") && !lowerId.includes("gad"))
                return "Anxiety Assessment";

              // Fallback to formatted ID
              return id
                .replace(/[-_]/g, " ")
                .replace(/\b\w/g, (l) => l.toUpperCase());
            };

            return {
              id: template.id,
              name: template.full_name || template.name || generateNameFromId(template.id),
              type: template.id,
              title: template.full_name || template.name || generateNameFromId(template.id),
              source_citations: template.source_citations,
              questions: processedQuestions,
            };
          })
          .filter((template, index, self) => {
            // Additional filter to remove duplicates based on ID
            return (
              template.questions.length > 0 &&
              self.findIndex((t) => t.id === template.id) === index
            );
          });

        console.log("Processed templates (final):", processedTemplates);

        setAvailableTests(processedTemplates);

        // Initialize progress
        const initialProgress = processedTemplates.map((template) => ({
          templateId: template.id,
          name: template.name,
          type: template.type,
          title: template.title,
          questions: template.questions.map((q: any) => ({
            ...q,
            selected: null,
          })),
          completed: false,
        }));

        setTestsProgress(initialProgress);

        // If only one test, go directly to questionnaire
        if (processedTemplates.length === 1) {
          setCurrentStep("questionnaire");
        }
      } catch (err: any) {
        console.error("Error loading patient tests:", err);
        setError(err.message || "Failed to load questionnaires");
        toast.error(err.message || "Failed to load questionnaires");
      } finally {
        setIsLoadingTests(false);
      }
    };

    loadPatientTests();
  }, [patientId]);

  const getCurrentTest = () => {
    return testsProgress[currentTestIndex] || null;
  };

  const canContinue = () => {
    const currentTest = getCurrentTest();
    if (!currentTest) return false;
    return currentTest.questions.every(
      (q: any) => q.selected !== null && q.selected !== undefined,
    );
  };

  const needsComment = (question: any, value: number) => {
    const option = question.options.find(
      (opt: any) => opt.value === value,
    );
    return option?.additional_input || false;
  };

  const handleResponse = (questionId: string, value: number) => {
    const currentTest = getCurrentTest();
    if (!currentTest) return;

    const question = currentTest.questions.find(
      (q: any) => q.id === questionId,
    );

    if (question && needsComment(question, value)) {
      setCurrentQuestionForComment({
        ...question,
        selectedValue: value,
      });
      setCommentText(question.comment || "");
    } else {
      setTestsProgress((prev) => {
        const updated = [...prev];
        if (updated[currentTestIndex]) {
          updated[currentTestIndex].questions = updated[
            currentTestIndex
          ].questions.map((q: any) =>
            q.id === questionId ? { ...q, selected: value, comment: "" } : q,
          );
        }
        return updated;
      });
    }
  };

  const completeCurrentTest = () => {
    const currentTest = getCurrentTest();
    if (!currentTest) return;

    const totalScore = currentTest.questions.reduce((sum: number, q: any) => {
      return sum + (typeof q.selected === "number" ? q.selected : 0);
    }, 0);

    setTestsProgress((prev) => {
      const updated = [...prev];
      if (updated[currentTestIndex]) {
        updated[currentTestIndex].completed = true;
        updated[currentTestIndex].totalScore = totalScore;
      }
      return updated;
    });

    if (currentTestIndex >= testsProgress.length - 1) {
      setAllTestsCompleted(true);
      setCurrentStep("review");
    } else {
      setCurrentTestIndex((prev) => prev + 1);
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      // Explicitly request audio/webm — browser default is webm, not wav
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : MediaRecorder.isTypeSupported('audio/webm')
          ? 'audio/webm'
          : '';
      const mediaRecorder = mimeType
        ? new MediaRecorder(stream, { mimeType })
        : new MediaRecorder(stream);
      const recordedMime = mediaRecorder.mimeType || 'audio/webm';
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event: BlobEvent) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        setIsProcessingAudio(true);

        try {
          const rawBlob = new Blob(audioChunksRef.current, { type: recordedMime });

          // Server only supports WAV/MP3/OGG — convert webm to WAV
          let audioFile: File;
          try {
            const wavBlob = await convertBlobToWav(rawBlob);
            audioFile = new File([wavBlob], `recording_${Date.now()}.wav`, { type: 'audio/wav' });
            console.log('Converted audio to WAV:', audioFile.size, 'bytes');
          } catch (convErr) {
            console.warn('WAV conversion failed, sending raw blob:', convErr);
            const ext = recordedMime.includes('webm') ? 'webm' : 'wav';
            audioFile = new File([rawBlob], `recording_${Date.now()}.${ext}`, { type: recordedMime });
          }

          const locationObj = locations.find(l => l.value === selectedLocationId);
          const locationLabel = locationObj ? locationObj.label : "Unknown — Unknown";
          const labelParts = locationLabel.split(" — ");
          // label is formatted as `${hospitalName} — ${branchName}`
          const parsedHospitalName = labelParts[0] || locationObj?.hospitalName || "";
          const parsedBranchName = labelParts[1] || locationObj?.branchName || "";

          // Transcribe audio
          const accessCode = new URLSearchParams(window.location.search).get('access_code') || '';
          const response = await biometricAuthService.transcribeAudio({
            audioFile,
            branch: parsedBranchName,
            hospital_name: parsedHospitalName,
            uuid: patientId,
            survey_reference: getCurrentTest()?.templateId || getCurrentTest()?.type || '',
            access_code: accessCode,  // patient SMS flow — no JWT needed
          });

          stream.getTracks().forEach((track) => track.stop());

          if (response.success && response.text) {
            const detectedLang = response.audioInfo?.detected_language;
            let displayText = response.text;
            let originalText = "";
            let originalLanguage = "";
            let segments: Array<{ start: number; end: number; text: string }> =
              [];

            if (response.transcription) {
              if (
                detectedLang === "es" &&
                response.transcription.spanish_full_text
              ) {
                originalText = response.transcription.spanish_full_text;
                originalLanguage = "es";
                displayText =
                  response.transcription.english?.full_text || originalText;
                segments = response.transcription.english?.segments || [];
              } else if (response.transcription.english) {
                originalText = response.transcription.english.full_text;
                originalLanguage = detectedLang === "es" ? "es" : "en";
                displayText = originalText;
                segments = response.transcription.english.segments || [];
              }
            }

            toast.success(
              `Audio transcribed successfully (${(detectedLang || "en").toUpperCase()})`,
            );
            setCommentText(displayText);
            setTranscription({
              text: displayText,
              segments: segments,
              language: detectedLang,
              originalLanguage: originalLanguage,
              originalText: originalText,
              audioFileUrl: response.fileInfo?.url || "",
              s3Key: response.fileInfo?.s3Key || ""
            });
          } else {
            toast.error(response.error || "Failed to transcribe audio");
          }
        } catch (error) {
          console.error("Error processing audio:", error);
          toast.error("Error processing audio");
        } finally {
          setIsProcessingAudio(false);
        }
      };

      mediaRecorder.start();
      setIsRecording(true);
      setRecordingTimeLeft(15);

      countdownTimerRef.current = setInterval(() => {
        setRecordingTimeLeft((prev) => {
          if (prev <= 1) {
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      recordingTimerRef.current = setTimeout(() => {
        if (mediaRecorder.state !== "inactive") {
          mediaRecorder.stop();
          setIsRecording(false);
          setRecordingTimeLeft(0);
          if (countdownTimerRef.current) {
            clearInterval(countdownTimerRef.current);
          }
          toast.info("Recording stopped automatically after 15 seconds");
        }
      }, 15000);
    } catch (err) {
      console.error("Error accessing microphone:", err);
      toast.error("Could not access microphone");
    }
  };

  const stopRecording = () => {
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state !== "inactive"
    ) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      setRecordingTimeLeft(0);

      if (recordingTimerRef.current) {
        clearTimeout(recordingTimerRef.current);
      }
      if (countdownTimerRef.current) {
        clearInterval(countdownTimerRef.current);
      }
    }
  };

  const saveComment = () => {
    if (!currentQuestionForComment) return;

    setTestsProgress((prev) => {
      const updated = [...prev];
      if (updated[currentTestIndex]) {
        updated[currentTestIndex].questions = updated[
          currentTestIndex
        ].questions.map((question: any) =>
          question.id === currentQuestionForComment.id
            ? {
              ...question,
              comment: commentText.trim(),
              selected: currentQuestionForComment.selectedValue,
              ...(transcription && {
                audio_file_url: transcription.s3Key || transcription.audioFileUrl,
                original_detected_language: transcription.originalLanguage,
                original_language_comment_transcribed:
                  transcription.originalText,
              }),
            }
            : question,
        );
      }
      return updated;
    });

    closeCommentModal();
  };

  const closeCommentModal = () => {
    if (
      isRecording &&
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state !== "inactive"
    ) {
      mediaRecorderRef.current.stop();
    }

    if (recordingTimerRef.current) {
      clearTimeout(recordingTimerRef.current);
    }
    if (countdownTimerRef.current) {
      clearInterval(countdownTimerRef.current);
    }

    setCurrentQuestionForComment(null);
    setCommentText("");
    setTranscription(null);
    setIsProcessingAudio(false);
    setRecordingTimeLeft(0);
  };

  const handleFormSubmit = async () => {
    setLoading(true);
    try {
      // Submit all completed questionnaires
      const submissions = testsProgress.map((test) => ({
        patient_id: patientId,
        questionnaire_type: test.type,
        answers: JSON.stringify(test.questions),
        total_score: test.totalScore || 0,
        self_reported: true,
        status: "pending for review",
      }));

      const submitData = submissions.map((s) => ({
        ...s,
        answers: typeof s.answers === "string" ? s.answers : JSON.stringify(s.answers),
      }));
      const result = await AuthService.submitQuestionnaireResponse(Array.isArray(submitData) && submitData.length === 1 ? submitData[0] : submitData);

      if (!result?.error) {
        const acCode = new URLSearchParams(window.location.search).get('access_code');
        const record = acCode
          ? await AuthService.getPublicPatient(patientId, acCode)
          : await AuthService.getPatient(patientId);
        if (record) {
          for (const test of testsProgress) {
            try {
              await AuthService.sendResendQuestionnaireEmail({
                type: test.type,
                form_data: record,
                total_score: test.totalScore || 0,
                questions: test.questions,
              });
            } catch (emailError) {
              console.log("Email error for", test.type, ":", emailError);
            }
          }
        }
        setCurrentStep("complete");
        toast.success("All questionnaires submitted successfully!");
      } else {
        toast.error((result.error as any)?.message || "Failed to submit");
      }
    } catch (e: any) {
      toast.error(
        `Error saving questionnaire responses. Please try again. ${e.message}`,
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteFromFinalStep = () => {
    setCurrentStep("selection");
    setTestsProgress([]);
    setCurrentTestIndex(0);
    setAllTestsCompleted(false);
    onComplete();
  };

  // Render test selection
  const renderSelection = () => {
    const completedCount = testsProgress.filter(
      (test) => test.completed,
    ).length;

    return (
      <Card className="w-full max-w-2xl mx-auto bg-white shadow-sm rounded-2xl overflow-hidden border border-gray-100">
        <CardHeader className="bg-[#1a7a4a] p-6 rounded-t-2xl">
          <CardTitle className="text-3xl font-bold text-white mb-2 tracking-tight">
            Welcome {patientName}
          </CardTitle>
          <p className="text-white/90 text-sm">
            You have {availableTests.length} questionnaire
            {availableTests.length > 1 ? "s" : ""} to complete
          </p>
          <div className="w-full bg-white/30 rounded-full h-2 mt-4">
            <div
              style={{
                width: `${(completedCount / availableTests.length) * 100}%`,
              }}
              className="h-2 rounded-full bg-white transition-all duration-300"
            />
          </div>
        </CardHeader>

        <CardContent className="p-6 space-y-4">
          {testsProgress.map((test, index) => (
            <div
              key={test.templateId}
              className={`p-4 rounded-xl border transition-all duration-200 ${test.completed
                ? "bg-[#e8f5ee] border-[#1a7a4a]/30"
                : index === currentTestIndex
                  ? "bg-white border-[#1a7a4a] shadow-sm shadow-[#1a7a4a]/10"
                  : "bg-gray-50 border-gray-100"
                }`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-gray-800">{test.name}</h3>
                  <p className="text-sm text-gray-600">
                    {test.completed
                      ? `Completed - Score: ${test.totalScore}`
                      : index === currentTestIndex
                        ? "In Progress"
                        : "Pending"}
                  </p>
                </div>
                <div className="flex items-center">
                  {test.completed ? (
                    <Check className="w-6 h-6 text-[#1a7a4a]" />
                  ) : index === currentTestIndex ? (
                    <div className="w-3 h-3 bg-[#1a7a4a] rounded-full animate-pulse" />
                  ) : (
                    <Circle className="w-6 h-6 text-gray-300" />
                  )}
                </div>
              </div>
            </div>
          ))}

          <div className="pt-4">
            {allTestsCompleted ? (
              <Button
                onClick={() => setCurrentStep("review")}
                className="w-full bg-[#1a7a4a] hover:bg-[#155e39] text-white shadow-md shadow-[#1a7a4a]/20 rounded-xl font-semibold"
              >
                Review All Answers
                <ChevronRight className="ml-2 w-4 h-4" />
              </Button>
            ) : (
              <Button
                onClick={() => setCurrentStep("questionnaire")}
                className="w-full bg-[#1a7a4a] hover:bg-[#155e39] text-white shadow-md shadow-[#1a7a4a]/20 rounded-xl font-semibold"
              >
                {currentTestIndex > 0
                  ? "Continue Next Test"
                  : "Start Questionnaires"}
                <ChevronRight className="ml-2 w-4 h-4" />
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  const renderQuestionnaire = () => {
    const currentTest = getCurrentTest();
    if (!currentTest) return null;

    const completedQuestions = currentTest.questions.filter(
      (q: any) => q.selected !== null && q.selected !== undefined,
    ).length;

    return (
      <Card className="w-full bg-white shadow-sm rounded-2xl overflow-hidden border border-gray-100 py-0">
        <CardHeader className="bg-[#1a7a4a] p-6 rounded-t-2xl">
          <div className="flex items-center mb-4">
            <FileText className="mr-3 text-white" size={28} />
            <CardTitle className="text-3xl font-bold text-white tracking-tight">
              {currentTest.title}
            </CardTitle>
          </div>
          <div className="w-full bg-white/30 rounded-full h-2">
            <div
              style={{
                width: `${(completedQuestions / currentTest.questions.length) * 100}%`,
              }}
              className="h-2 rounded-full bg-white transition-all duration-300"
            ></div>
          </div>
          <p className="text-sm text-white mt-2">
            Test {currentTestIndex + 1} of {availableTests.length} - Over the
            last 2 weeks, how often have you been bothered by any of the
            following problems?
          </p>
          {currentTest.source_citations && (
            <p className="text-xs text-white/80 mt-1 italic">
              Citation: {typeof currentTest.source_citations === 'string' ? currentTest.source_citations : JSON.stringify(currentTest.source_citations)}
            </p>
          )}
        </CardHeader>

        <CardContent className="space-y-6 p-6">
          {currentTest.questions.map((q: any, index: number) => {
            return (
              <div
                key={q.id}
                className="bg-white p-5 rounded-2xl border border-gray-200 shadow-sm transition-all hover:border-[#1a7a4a]/30"
              >
                <p className="font-semibold text-gray-900 mb-4 text-base">
                  {index + 1}. {q.question}
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                  {q.options.map((option: any) => {
                    const isSelected = q.selected === option.value;
                    const isAdditionalInput = option.additional_input;

                    return (
                      <div
                        key={option.value}
                        onClick={() => handleResponse(q.id, option.value)}
                        className={`flex items-center justify-start gap-3 px-4 py-3 rounded-xl cursor-pointer border transition-all duration-200 text-sm font-medium ${isSelected
                          ? "bg-[#e8f5ee] border-[#1a7a4a] text-[#155e39] shadow-inner ring-1 ring-[#1a7a4a]/50"
                          : "bg-white border-gray-200 hover:bg-gray-50 text-gray-600 hover:border-gray-300"
                          } ${isAdditionalInput ? "relative" : ""}`}
                      >
                        {isSelected ? (
                          <Check className="text-[#1a7a4a] shrink-0" size={18} />
                        ) : (
                          <Circle className="text-gray-300 shrink-0" size={18} />
                        )}
                        <span className="text-left">{option.label}</span>
                        {isAdditionalInput && !isSelected && (
                          <div className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-[#1a7a4a]/50 rounded-full flex items-center justify-center">
                            <span className="text-white text-[10px]">💬</span>
                          </div>
                        )}
                        {isSelected && q.comment && (
                          <div className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-[#1a7a4a] rounded-full flex items-center justify-center shadow-sm">
                            <FileText className="text-white" size={10} />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                {q.audio_file_url && (
                  <div className="flex items-center gap-2 text-xs text-[#1a7a4a] mt-1">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="flex-shrink-0"
                    >
                      <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"></polygon>
                      <path d="M15.54 8.46a5 5 0 0 1 0 7.07"></path>
                      <path d="M19.07 4.93a10 10 0 0 1 0 14.14"></path>
                    </svg>
                    <span>Audio response saved</span>
                  </div>
                )}
                {q.comment && (
                  <div className="mt-4 p-4 bg-[#f8faf9] rounded-xl border border-gray-200/60">
                    <p className="text-sm font-semibold text-gray-900 mb-1">
                      Additional Details:
                    </p>
                    <p className="text-sm text-gray-600 whitespace-pre-wrap">{q.comment}</p>
                  </div>
                )}
              </div>
            );
          })}
        </CardContent>

        <CardFooter className="p-6 border-t border-gray-100 mt-2">
          <div className="flex flex-col md:flex-row justify-between gap-4 w-full">
            <Button
              onClick={() => setCurrentStep("selection")}
              variant="outline"
              className="w-full md:w-auto bg-white border-gray-200 hover:bg-gray-50 text-gray-700 rounded-xl font-medium"
            >
              <ChevronLeft className="mr-2" size={16} />
              Back to Overview
            </Button>

            <Button
              onClick={completeCurrentTest}
              disabled={!canContinue()}
              className="w-full md:w-auto bg-[#1a7a4a] hover:bg-[#155e39] text-white shadow-md shadow-[#1a7a4a]/20 rounded-xl font-semibold"
            >
              {currentTestIndex >= testsProgress.length - 1 ? (
                <>
                  Complete Test <Check className="ml-2" size={16} />
                </>
              ) : (
                <>
                  Next Test <ChevronRight className="ml-2" size={16} />
                </>
              )}
            </Button>
          </div>
        </CardFooter>
      </Card>
    );
  };

  const renderReview = () => {
    const totalCombinedScore = testsProgress.reduce(
      (sum, test) => sum + (test.totalScore || 0),
      0,
    );

    return (
      <Card className="max-w-4xl mx-auto bg-white shadow-sm rounded-2xl overflow-hidden border border-gray-100 py-0">
        <CardHeader className="bg-[#1a7a4a] p-6 rounded-t-2xl">
          <CardTitle className="text-3xl font-bold text-white mb-2 tracking-tight">
            Review All Your Answers
          </CardTitle>
          <div className="w-full bg-white/30 rounded-full h-2">
            <div className="bg-white h-2 rounded-full w-full"></div>
          </div>
          <p className="text-white/90 text-sm mt-2">
            All {availableTests.length} questionnaire
            {availableTests.length > 1 ? "s" : ""} completed
          </p>
        </CardHeader>

        <CardContent className="space-y-8 p-6">
          {testsProgress.map((test, testIndex) => (
            <div key={test.templateId} className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-gray-900">{test.name}</h3>
                <span className="bg-[#e8f5ee] text-[#155e39] px-3 py-1 rounded-full text-sm font-semibold border border-[#1a7a4a]/10">
                  Score: {test.totalScore}
                </span>
              </div>

              {test.questions.map((q: any, index: number) => (
                <div
                  key={q.id}
                  className="bg-white p-4 rounded-xl border border-gray-200"
                >
                  <p className="font-medium text-gray-900 mb-2 text-sm">
                    {index + 1}. {q.question}
                  </p>
                  <p className="text-[#1a7a4a] font-medium text-sm">
                    {q.options.find((opt: any) => opt.value === q.selected)
                      ?.label || "Not answered"}
                  </p>
                  {q.comment && (
                    <div className="mt-3 p-3 bg-gray-50 rounded-lg border border-gray-100">
                      <p className="text-xs text-gray-800 font-semibold uppercase tracking-wider mb-1">
                        Additional Details:
                      </p>
                      <p className="text-sm text-gray-600 m-0">{q.comment}</p>
                    </div>
                  )}
                </div>
              ))}

              {testIndex < testsProgress.length - 1 && <Separator className="bg-gray-100" />}
            </div>
          ))}

          <Separator className="bg-gray-100" />

          <div className="bg-[#e8f5ee] p-5 rounded-2xl shadow-sm border border-[#1a7a4a]/10 mt-6">
            <div className="flex justify-between items-center">
              <p className="font-semibold text-gray-900 text-lg">
                Total Combined Score
              </p>
              <p className="font-bold text-[#1a7a4a] text-2xl">
                {totalCombinedScore}
              </p>
            </div>
          </div>
        </CardContent>

        <CardFooter className="p-6 border-t border-gray-100">
          <div className="flex flex-col md:flex-row justify-between gap-4 w-full">
            <Button
              onClick={() => setCurrentStep("selection")}
              variant="outline"
              className="w-full md:w-auto bg-white border-gray-200 hover:bg-gray-50 text-gray-700 rounded-xl font-medium"
            >
              <ArrowLeft className="mr-2" size={16} />
              Back to Overview
            </Button>

            <Button
              onClick={handleFormSubmit}
              disabled={loading}
              className="w-full md:w-auto bg-[#1a7a4a] hover:bg-[#155e39] text-white shadow-md shadow-[#1a7a4a]/20 rounded-xl font-semibold"
            >
              {loading ? (
                <div className="spinner h-5 w-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              ) : (
                <>
                  Submit All Answers <Check className="ml-2" size={16} />
                </>
              )}
            </Button>
          </div>
        </CardFooter>
      </Card>
    );
  };

  const renderComplete = () => (
    <Card className="max-w-2xl mx-auto text-center bg-white shadow-sm rounded-2xl border border-gray-100 overflow-hidden py-0">
      <CardHeader className="bg-[#f7f8fa] p-8 rounded-t-2xl border-b border-gray-100">
        <div className="w-20 h-20 bg-[#e8f5ee] rounded-full flex items-center justify-center mx-auto mb-6 shadow-sm border border-[#1a7a4a]/10">
          <Check className="text-[#1a7a4a]" size={40} />
        </div>
        <CardTitle className="text-3xl font-bold text-gray-900 tracking-tight mb-2">
          All Questionnaires Completed!
        </CardTitle>
        <p className="text-gray-500">
          Thank you {patientName} for completing all {availableTests.length}{" "}
          assigned questionnaire{availableTests.length > 1 ? "s" : ""}.
        </p>
      </CardHeader>

      <CardContent className="p-8 space-y-6">
        <div className="bg-gray-50 p-5 rounded-2xl border border-gray-100/60 shadow-sm text-left">
          <p className="text-sm text-gray-700 mb-2">
            <strong className="text-gray-900">Questionnaires Completed:</strong>{" "}
            {availableTests.map((t) => t.name).join(", ")}
          </p>
          <p className="text-sm text-gray-600">
            Your responses have been securely saved and will be reviewed by your
            healthcare provider.
          </p>
        </div>

        <div className="bg-[#e8f5ee]/50 p-5 rounded-2xl border border-[#1a7a4a]/10 shadow-sm text-left">
          <p className="text-sm text-[#155e39]">
            <strong>Next Steps:</strong> Your healthcare provider will review
            your responses and may contact you to discuss the results or
            schedule a follow-up appointment.
          </p>
        </div>

        <Button
          onClick={handleCompleteFromFinalStep}
          className={cn(
            "w-full mt-6 h-14 text-base font-semibold text-white",
            "bg-[#1a7a4a] hover:bg-[#155e39]",
            "rounded-xl shadow-md shadow-[#1a7a4a]/20",
            "hover:scale-[1.02] transition-transform duration-150 ease-in-out",
            "disabled:opacity-60 disabled:cursor-not-allowed",
          )}
        >
          Continue
        </Button>
      </CardContent>
    </Card>
  );

  // Loading state
  if (isLoadingTests) {
    return (
      <div className="min-h-screen bg-[#f7f8fa] flex items-center justify-center font-sans">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-[#1a7a4a] mx-auto mb-4" />
          <p className="text-gray-500 font-medium tracking-tight">Loading assigned questionnaires...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-[#f7f8fa] flex items-center justify-center font-sans">
        <div className="text-center py-12 max-w-sm mx-auto bg-white p-8 rounded-2xl shadow-sm border border-gray-100">
          <p className="text-red-500 font-medium mb-6">{error}</p>
          <Button onClick={() => window.location.reload()} variant="outline" className="w-full rounded-xl">
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f7f8fa] font-sans">
      {/* Comment Modal */}
      <Dialog
        open={!!currentQuestionForComment}
        onOpenChange={(open) => !open && closeCommentModal()}
      >
        <DialogContent className="sm:max-w-[500px] max-h-[90vh] flex flex-col p-4 sm:p-6 w-[95vw] sm:w-full">
          <DialogHeader className="shrink-0">
            <DialogTitle>Additional Details</DialogTitle>
            <DialogDescription className="sr-only">
              Provide additional details or record audio for your response.
            </DialogDescription>
          </DialogHeader>

          <div className="overflow-y-auto flex-1 px-1 pb-1 min-h-0 space-y-4 -mx-1 -mb-1">
            <div className="space-y-2">
              <p className="font-medium text-foreground">Question:</p>
              <p className="text-sm text-muted-foreground bg-muted/50 p-3 rounded-md">
                {currentQuestionForComment?.question}
              </p>
            </div>
            <div className="space-y-2">
              <p className="font-medium text-foreground">Selected Answer:</p>
              <p className="text-sm text-muted-foreground bg-muted/50 p-3 rounded-md">
                {
                  currentQuestionForComment?.options.find(
                    (opt: any) =>
                      opt.value === currentQuestionForComment?.selectedValue,
                  )?.label
                }
              </p>
            </div>
            <div className="space-y-4">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 sm:gap-0 mb-2">
                <p className="text-sm font-medium text-gray-700">
                  Additional Details:
                </p>
                <Button
                  type="button"
                  variant={isRecording ? "destructive" : "outline"}
                  size="sm"
                  onClick={isRecording ? stopRecording : startRecording}
                  disabled={isProcessingAudio}
                  className="gap-2 shrink-0 self-start sm:self-auto"
                >
                  {isProcessingAudio ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />{" "}
                      Processing...
                    </>
                  ) : isRecording ? (
                    <>
                      <StopCircle className="h-4 w-4" /> Stop (
                      {recordingTimeLeft}s)
                    </>
                  ) : (
                    <>
                      <Mic className="h-4 w-4" /> Record Audio
                    </>
                  )}
                </Button>
              </div>

              {isProcessingAudio && (
                <div className="flex items-center gap-2 text-sm text-blue-600 bg-blue-50 p-3 rounded-md border border-blue-200">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Transcribing audio, please wait...</span>
                </div>
              )}

              {isRecording && (
                <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 p-3 rounded-md border border-red-200">
                  <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                  <span>
                    Recording... Auto-stop in {recordingTimeLeft} seconds
                  </span>
                </div>
              )}

              {transcription && (
                <div className="mb-4 p-3 bg-gray-50 rounded-md border border-gray-200 max-h-48 overflow-y-auto">
                  <div className="flex justify-between items-center mb-2">
                    <h4 className="font-medium">Transcription</h4>
                    {transcription.language && (
                      <span className="text-xs text-gray-500">
                        Detected: {transcription.language.toUpperCase()}
                        {transcription.originalLanguage &&
                          transcription.originalLanguage !==
                          transcription.language &&
                          ` | Original: ${transcription.originalLanguage.toUpperCase()}`}
                      </span>
                    )}
                  </div>

                  {transcription.originalText &&
                    transcription.originalLanguage !== "en" && (
                      <div className="mb-3 p-2 bg-blue-50 rounded border border-blue-200">
                        <p className="text-xs font-medium text-blue-700 mb-1">
                          Original (
                          {transcription.originalLanguage?.toUpperCase()}):
                        </p>
                        <div className="text-sm text-blue-800 whitespace-pre-wrap">
                          {transcription.originalText}
                        </div>
                      </div>
                    )}

                  <div className="text-sm text-gray-700 whitespace-pre-wrap">
                    {transcription.originalLanguage &&
                      transcription.originalLanguage !== "en" && (
                        <p className="text-xs font-medium text-gray-600 mb-1">
                          English Translation:
                        </p>
                      )}
                    {transcription.text}
                  </div>

                  {transcription.segments &&
                    transcription.segments.length > 0 && (
                      <div className="mt-3 text-xs text-gray-500">
                        <p className="font-medium mb-1">Timing segments:</p>
                        {transcription.segments.map((segment, i) => (
                          <div key={i} className="mb-1">
                            <span className="text-gray-400">
                              [{segment.start.toFixed(1)}s-
                              {segment.end.toFixed(1)}s]:
                            </span>{" "}
                            {segment.text}
                          </div>
                        ))}
                      </div>
                    )}
                </div>
              )}

              <Textarea
                placeholder="Please provide more details about your response..."
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                rows={5}
                disabled={isProcessingAudio || isRecording}
                className="resize-none mt-2 outline-none focus-visible:ring-1 focus-visible:ring-green-500 border-gray-200"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-4 shrink-0 sm:pt-6">
            <Button
              variant="outline"
              onClick={closeCommentModal}
              disabled={isProcessingAudio || isRecording}
            >
              {isRecording ? "Stop Recording First" : "Cancel"}
            </Button>
            <Button
              onClick={saveComment}
              disabled={isProcessingAudio || isRecording}
              className="bg-gray-900 text-white hover:bg-gray-800"
            >
              {isProcessingAudio ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Processing...
                </>
              ) : (
                "Save Details"
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <div className="container mx-auto px-4 py-8">
        {currentStep === "selection" && renderSelection()}
        {currentStep === "questionnaire" && renderQuestionnaire()}
        {currentStep === "review" && renderReview()}
        {currentStep === "complete" && renderComplete()}
      </div>
    </div>
  );
};

// Main Wrapper Component
// export default function PatientQuestionnaireWrapper() {
//   const { patientId } = useParams<{ patientId: string }>();
//   const navigate = useNavigate();
//   const [currentStep, setCurrentStep] = useState(1);
//   const [patient, setPatient] = useState<Patient | null>(null);
//   const [loading, setLoading] = useState(true);
//   const [currentQuestionnaire, setCurrentQuestionnaire] = useState<string>("");
//   // Tests will be loaded from the server
//   const [availableTests, setAvailableTests] = useState<
//     Array<{ id: string; name: string; questions: any[] }>
//   >([]);

//   // Load tests when component mounts
//   useEffect(() => {
//     // This would be an API call in a real app

//     const loadTests = async () => {
//       try {
//         const { data, error } = await supabase
//           .from("patients")
//           .select("selected_tests")
//           .eq("id", patientId)
//           .single();

//         if (error) {
//           console.error("Error loading tests:", error);
//           return;
//         }

//         setAvailableTests(data.selected_tests);
//         console.log("Available tests:", availableTests);
//       } catch (error) {
//         console.error("Error loading tests:", error);
//       }
//     };

//     loadTests();
//   }, []);

//   // Generate steps based on available tests
//   const getSteps = useCallback(() => {
//     return [
//       {
//         id: "hipaa",
//         title: "HIPAA",
//         icon: <FileText className="w-4 h-4" />,
//         isTestStep: false,
//       },
//       {
//         id: "verify-id",
//         title: "Verify ID",
//         icon: <Camera className="w-4 h-4" />,
//         isTestStep: false,
//       },
//       {
//         id: "tests",
//         title: "Tests",
//         icon: <ClipboardList className="w-4 h-4" />,
//         isTestStep: true,
//       },
//       {
//         id: "done",
//         title: "Done",
//         icon: <Check className="w-4 h-4" />,
//         isTestStep: false,
//       },
//     ];
//   }, []);

//   const steps = getSteps();

//   useEffect(() => {
//     const fetchPatient = async () => {
//       if (!patientId) {
//         toast.error("Patient ID is required");
//         navigate("/");
//         return;
//       }

//       try {
//         // First, fetch the patient data
//         const { data, error } = await supabase
//           .from("patients")
//           .select("*")
//           .eq("id", patientId)
//           .single();

//         if (error) {
//           console.error("Error fetching patient:", error);
//           throw new Error("Failed to fetch patient data");
//         }

//         // Check if patient has any existing encounters
//         const { data: existingEncounters } = await supabase
//           .from("patient_encounter")
//           .select("*")
//           .eq("patient_id", patientId)
//           .limit(1);

//         let encounter = existingEncounters?.[0];

//         // Only create a new encounter if none exists
//         if (!encounter) {
//           const newEncounterData = {
//             patient_id: patientId,
//             encounter_type: "SMS_Survey",
//             status: "completed",
//             encounter_date: new Date().toISOString(),
//             encounter_id: `SMS-${Date.now()}-${patientId.substring(0, 8)}`,
//             location: "Remote",
//             provider_id: "system",
//             notes: "Patient initiated SMS survey",
//             // Add required fields from the sample encounter
//             department: null,
//             chief_complaint: null,
//             diagnosis_primary: null,
//             visit_reason: "SMS Survey Response",
//             visit_priority: "routine",
//             clinical_notes: "Patient completed SMS survey",
//           };

//           // Insert the new encounter
//           const { data: newEncounter, error: insertError } = await supabase
//             .from("patient_encounter")
//             .insert([newEncounterData])
//             .select()
//             .single();

//           if (insertError) {
//             console.warn("Error creating new encounter:", insertError);
//             // Continue with a minimal encounter object if creation fails
//             encounter = {
//               id: `temp-${Date.now()}`,
//               patient_id: patientId,
//               encounter_type: "SMS_Survey",
//               status: "completed",
//               encounter_date: new Date().toISOString(),
//               encounter_id: `SMS-temp-${patientId.substring(0, 8)}`,
//             };
//           } else if (newEncounter) {
//             encounter = newEncounter;
//           }
//         }

//         // Prepare the new consent log entry
//         const newConsentLog = {
//           uuid: encounter.encounter_id,
//           timestamp: new Date().toISOString(),
//           status: "accepted",
//           type: "sms_survey_consent",
//           details: "Patient consented to participate in SMS survey",
//         };

//         // Get existing consent logs or initialize as empty array
//         const existingLogs = Array.isArray(data.consent_logs)
//           ? data.consent_logs
//           : [];

//         // Add the new consent log
//         const updatedConsentLogs = [...existingLogs, newConsentLog];

//         // Update the patient with the new consent log
//         const { error: updateError } = await supabase
//           .from("patients")
//           .update({
//             consent_logs: updatedConsentLogs,
//             //last_updated: new Date().toISOString()
//           })
//           .eq("id", patientId);

//         if (updateError) throw updateError;

//         // Update the patient enquiry
//         const enquiryData = {
//           patient_id: patientId,
//           enquiry_type: "SMS_Survey",
//           status: "completed",
//           notes: "Patient completed SMS survey",
//           encounter_id: encounter.encounter_id,
//         };

//         const { error: enquiryError } = await supabase
//           .from("patient_enquiries")
//           .insert(enquiryData);

//         if (enquiryError)
//           console.error("Error updating patient enquiry:", enquiryError);

//         // Refresh patient data with updated consent logs
//         const { data: updatedPatient, error: fetchError } = await supabase
//           .from("patients")
//           .select("*")
//           .eq("id", patientId)
//           .single();

//         if (fetchError) throw fetchError;

//         setPatient(updatedPatient);

//         // Determine starting step based on patient data
//         if (updatedPatient.hipaa_email_and_sms_consent) {
//           setCurrentStep(1); // Skip to biometric verification if HIPAA already consented
//         }
//       } catch (error) {
//         console.error("Error in fetchPatient:", error);
//         // If we have patient data but failed to create encounter, still allow proceeding
//         if (patient) {
//           setPatient(patient);
//           setLoading(false);
//           return;
//         }
//         toast.error(
//           error instanceof Error
//             ? error.message
//             : "An error occurred while loading patient data",
//         );
//       } finally {
//         setLoading(false);
//       }
//     };

//     fetchPatient();
//   }, [patientId, navigate]);

//   const handleNext = () => {
//     if (currentStep < 4) {
//       setCurrentStep(currentStep + 1);
//     }
//   };

//   const handleBack = () => {
//     if (currentStep > 1) {
//       setCurrentStep(currentStep - 1);
//     }
//   };

//   const handleQuestionnaireComplete = async () => {
//     if (currentQuestionnaire === "phq-9") {
//       // Move to GAD-7 after PHQ-9 is completed
//       setCurrentQuestionnaire("gad-7");
//       setCurrentStep(3); // Stay on step 3 but show GAD-7
//     } else {
//       // Both questionnaires are complete, move to completion step
//       setCurrentStep(4);
//     }
//   };

//   if (loading) {
//     return (
//       <div className="min-h-screen bg-gray-50">
//         <Header />
//         <div className="flex items-center justify-center min-h-[calc(100vh-80px)]">
//           <div className="text-center">
//             <div className="w-12 h-12 border-4 border-green-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
//             <p className="text-gray-600">Loading patient information...</p>
//           </div>
//         </div>
//       </div>
//     );
//   }

//   if (!patient) {
//     return (
//       <div className="min-h-screen bg-gray-50">
//         <Header />
//         <div className="flex items-center justify-center min-h-[calc(100vh-80px)]">
//           <Card className="max-w-md mx-auto">
//             <CardContent className="p-6 text-center">
//               <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
//               <h2 className="text-xl font-bold text-gray-800 mb-2">
//                 Patient Not Found
//               </h2>
//               <p className="text-gray-600 mb-4">
//                 The patient with ID "{patientId}" could not be found.
//               </p>
//               <Button onClick={() => navigate("/support")} className="w-full">
//                 Contact Support
//               </Button>
//             </CardContent>
//           </Card>
//         </div>
//       </div>
//     );
//   }

//   return (
//     <div className="min-h-screen bg-gradient-to-br from-green-50 via-blue-50 to-purple-50">
//       <Header />

//       <div className="container mx-auto px-3 sm:px-4 py-4 sm:py-6">
//         {/* Patient Information Header */}
//         <Card className="mb-4 sm:mb-6 bg-white shadow-sm border border-gray-200 rounded-xl sm:rounded-2xl overflow-hidden">
//           <CardContent className="p-3 sm:p-4 md:p-6">
//             <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
//               <div className="flex items-start sm:items-center gap-3">
//                 <div className="w-14 h-14 sm:w-16 sm:h-16 bg-green-500 rounded-full flex-shrink-0 flex items-center justify-center text-white font-bold text-lg sm:text-xl shadow-lg">
//                   {patient.first_name?.[0]}
//                   {patient.last_name?.[0]}
//                 </div>
//                 <div className="flex-1 min-w-0">
//                   <h1 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-800 truncate">
//                     {patient.first_name} {patient.last_name}
//                   </h1>
//                   <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs sm:text-sm text-gray-600 mt-1">
//                     {/* <span className="flex items-center gap-1 whitespace-nowrap">
//                       <User className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
//                       <span className="truncate max-w-[100px] sm:max-w-none">ID: {patient.id}</span>
//                     </span> */}
//                     {patient.mobile_number && (
//                       <span className="flex items-center gap-1 whitespace-nowrap">
//                         <Phone className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
//                         <span className="truncate max-w-[120px]">
//                           {patient.mobile_number}
//                         </span>
//                       </span>
//                     )}
//                     {patient.date_of_birth && (
//                       <span className="flex items-center gap-1 whitespace-nowrap">
//                         <Calendar className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
//                         <span className="truncate">
//                           {new Date(patient.date_of_birth).toLocaleDateString()}
//                         </span>
//                       </span>
//                     )}
//                   </div>
//                 </div>
//               </div>
//               <div className="text-right sm:text-left sm:pl-4 border-t pt-2 sm:border-t-0 sm:pt-0">
//                 <div className="text-xs sm:text-sm text-gray-500">Gender</div>
//                 <div className="font-medium text-sm sm:text-base text-gray-800 capitalize">
//                   {patient.gender || "Not specified"}
//                 </div>
//               </div>
//             </div>
//           </CardContent>
//         </Card>

//         {/* Stepper */}
//         <div className="max-w-4xl mx-auto mb-4 sm:mb-6 px-2">
//           <Stepper
//             currentStep={currentStep}
//             totalSteps={steps.length}
//             steps={steps}
//           />
//         </div>

//         {/* Step Content */}
//         <div className="max-w-4xl mx-auto px-2 sm:px-0">
//           {currentStep === 1 && (
//             <HIPAAConsentStep
//               patientName={patient.first_name + " " + patient.last_name}
//               patientId={patientId!}
//               onNext={handleNext}
//             />
//           )}

//           {currentStep === 2 && (
//             <BiometricVerificationStep
//               patientId={patientId!}
//               onNext={handleNext}
//               onBack={handleBack}
//             />
//           )}

//           {currentStep === 3 && (
//             <QuestionnaireStep
//               patientId={patientId!}
//               onComplete={handleQuestionnaireComplete}
//             />
//           )}
//           {currentStep === 4 && (
//             <div className="text-center py-8 sm:py-12 px-2">
//               <div className="bg-green-50 w-14 h-14 sm:w-16 sm:h-16 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6 border-2 border-green-200">
//                 <Check className="w-7 h-7 sm:w-8 sm:h-8 text-green-600" />
//               </div>
//               <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-2">
//                 Questionnaires Completed!
//               </h2>
//               <p className="text-gray-600 mb-6 text-sm sm:text-base">
//                 Thank you for completing both PHQ-9 and GAD-7 questionnaires.
//               </p>
//             </div>
//           )}
//         </div>
//       </div>
//     </div>
//   );
// }

interface PrimaryProvider {
  id: string;
  name: string;
  email: string;
  role: string;
}

export default function PatientQuestionnaireWrapper() {
  const { patientId } = useParams<{ patientId: string }>();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [surveyAlreadyCompleted, setSurveyAlreadyCompleted] = useState(false);
  const [patient, setPatient] = useState<Patient | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentQuestionnaire, setCurrentQuestionnaire] = useState<string>("");
  const [primaryProvider, setPrimaryProvider] =
    useState<PrimaryProvider | null>(null);

  const [existingResponseData, setExistingResponseData] = useState<any>(null);
  const [showRedoDialog, setShowRedoDialog] = useState(false);

  // Read patient data passed from QuestionnaireAccess via route state
  // This avoids calling the JWT-protected /rds/patients/:id endpoint
  const routerLocation = useRouterLocation();
  const routePatientData = (routerLocation.state as any)?.patientData || null;

  // Static support contact
  const supportContact = {
    name: "Admin",
    email: "Admin@monitorhealth.ai",
    role: "Admin Support",
  };

  // Check for existing questionnaire responses
  useEffect(() => {
    const checkExistingResponses = async () => {
      if (!patientId) return;

      try {
        const data = await AuthService.getQuestionnaireResponses({
          patient_id: patientId,
          page: 1,
          pageSize: 1,
        });
        const responses = Array.isArray(data) ? data : (data as any)?.data || [];
        const latest = responses[0];

        if (latest) {
          setExistingResponseData(latest);
          setShowRedoDialog(true);
        }
      } catch (error) {
        console.error("Error checking existing responses:", error);
      }
    };

    checkExistingResponses();
  }, [patientId]);

  // Fetch primary provider from most recent encounter
  useEffect(() => {
    const fetchPrimaryProvider = async () => {
      if (!patientId) return;

      try {
        const providers = await AuthService.getPatientProviders(patientId);
        if (providers?.length > 0) {
          setPrimaryProvider(providers[0]);
        }
      } catch (error) {
        console.error("Error fetching primary provider:", error);
      }
    };

    fetchPrimaryProvider();
  }, [patientId]);

  // Generate steps based on available tests
  const getSteps = useCallback(() => {
    return [
      {
        id: "hipaa",
        title: "HIPAA",
        icon: <FileText className="w-4 h-4" />,
        isTestStep: false,
      },
      {
        id: "verify-id",
        title: "Verify ID",
        icon: <Camera className="w-4 h-4" />,
        isTestStep: false,
      },
      {
        id: "tests",
        title: "Tests",
        icon: <ClipboardList className="w-4 h-4" />,
        isTestStep: true,
      },
      {
        id: "done",
        title: "Done",
        icon: <Check className="w-4 h-4" />,
        isTestStep: false,
      },
    ];
  }, []);

  const steps = getSteps();

  useEffect(() => {
    const fetchPatient = async () => {
      if (!patientId) {
        toast.error("Patient ID is required");
        navigate("/");
        return;
      }

      try {
        // Use patient data from route state if available (passed from QuestionnaireAccess).
        // Fall back to public endpoint using access code — avoids the JWT-protected call.
        const accessCode = new URLSearchParams(window.location.search).get('access_code');
        const data = routePatientData
          || (accessCode ? await AuthService.getPublicPatient(patientId, accessCode) : null)
          || await AuthService.getPatient(patientId);
        if (!data) {
          throw new Error("Failed to fetch patient data");
        }

        const isSMSFlow = !!new URLSearchParams(window.location.search).get('access_code');

        if (isSMSFlow) {
          // ── SMS flow: no JWT available ────────────────────────────────────────
          // Skip all JWT-protected calls (getPatientEncounters, createPatientEncounter,
          // updatePatient). Use the patient data already fetched above.
          setPatient(data);

          // Check if this patient already submitted a response for this SMS session.
          // hasExistingResponse is returned by verify_access and passed via route state.
          const hasExistingResponse = (routerLocation.state as any)?.hasExistingResponse || false;
          if (hasExistingResponse) {
            // Survey already completed for this SMS link — show completion screen.
            setSurveyAlreadyCompleted(true);
          } else {
            // Always start at step 1 (consent) — fresh consent required for every
            // SMS survey session for audit purposes.
            setCurrentStep(1);
          }
        } else {
          // ── Authenticated app flow: JWT present ───────────────────────────────
          const existingEncounters = await AuthService.getPatientEncounters({ patient_id: patientId });
          let encounter = existingEncounters?.[0];

          if (!encounter) {
            const encounterId = `SMS-${Date.now()}-${patientId.substring(0, 8)}`;
            const newEncounterData = {
              patient_id: patientId,
              encounter_type: "SMS_Survey",
              status: "completed",
              encounter_date: new Date().toISOString().split("T")[0],
              encounter_id: encounterId,
              visit_reason: "SMS Survey Response",
              visit_priority: "routine",
              clinical_notes: "Patient completed SMS survey",
            };

            try {
              const created = await AuthService.createPatientEncounter(newEncounterData);
              encounter = Array.isArray(created) ? created[0] : created;
            } catch (insertError) {
              console.warn("Error creating new encounter:", insertError);
            }
            if (!encounter) {
              encounter = {
                patient_id: patientId,
                encounter_type: "SMS_Survey",
                status: "completed",
                encounter_date: new Date().toISOString(),
                encounter_id: `SMS-temp-${patientId.substring(0, 8)}`,
              };
            }
          }

          const newConsentLog = {
            uuid: encounter.encounter_id,
            timestamp: new Date().toISOString(),
            status: "accepted",
            type: "sms_survey_consent",
            details: "Patient consented to participate in SMS survey",
          };

          const existingLogs = Array.isArray(data.consent_logs) ? data.consent_logs : [];
          const updatedConsentLogs = [...existingLogs, newConsentLog];

          await AuthService.updatePatient(patientId, { consent_logs: updatedConsentLogs });

          const updatedPatient = await AuthService.getPatient(patientId);
          setPatient(updatedPatient);

          if (updatedPatient?.hipaa_email_and_sms_consent) {
            setCurrentStep(1);
          }
        }
      } catch (error) {
        console.error("Error in fetchPatient:", error);
        if (patient) {
          setPatient(patient);
          setLoading(false);
          return;
        }
        toast.error(
          error instanceof Error
            ? error.message
            : "An error occurred while loading patient data",
        );
      } finally {
        setLoading(false);
      }
    };

    fetchPatient();
  }, [patientId, navigate]);

  const handleNext = () => {
    if (currentStep < 4) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleQuestionnaireComplete = async () => {
    if (currentQuestionnaire === "phq-9") {
      setCurrentQuestionnaire("gad-7");
      setCurrentStep(3);
    } else {
      setCurrentStep(4);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center min-h-[calc(100vh-80px)]">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-green-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-gray-600">Loading patient information...</p>
          </div>
        </div>
      </div>
    );
  }

  // ── Survey already completed for this SMS link ──────────────────────────────
  if (surveyAlreadyCompleted) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center min-h-[calc(100vh-80px)] px-4">
          <div className="text-center max-w-md">
            <div className="bg-green-50 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 border-2 border-green-200">
              <Check className="w-10 h-10 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-3">
              Survey Already Completed
            </h2>
            <p className="text-gray-600 mb-4">
              You have already submitted your responses for this survey link.
              Your answers have been recorded and shared with your care team.
            </p>
            <p className="text-sm text-gray-400">
              If you have questions, please contact your healthcare provider.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (!patient) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center min-h-[calc(100vh-80px)]">
          <Card className="max-w-md mx-auto">
            <CardContent className="p-6 text-center">
              <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
              <h2 className="text-xl font-bold text-gray-800 mb-2">
                Patient Not Found
              </h2>
              <p className="text-gray-600 mb-4">
                The patient with ID "{patientId}" could not be found.
              </p>
              <Button onClick={() => {
                window.location.href = "mailto:admin@monitorhealth.ai?subject=Support%20Request%20-%20Monitor%20Health%20AI&body=Hello%20Support%20Team,%0D%0A%0D%0AI%20need%20assistance%20with%20the%20following%20issue:%0D%0A";
              }} className="w-full">
                Contact Support
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-blue-50 to-purple-50">
      <Header />

      {/* ADD THIS DIALOG HERE */}
      <Dialog open={showRedoDialog} onOpenChange={setShowRedoDialog}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-amber-500" />
              Existing Survey Found
            </DialogTitle>
            <DialogDescription asChild>
              <div className="space-y-3 pt-2">
                <p className="text-gray-700">
                  You have already completed a{" "}
                  <span className="font-semibold text-gray-900">
                    {existingResponseData?.questionnaire_type}
                  </span>{" "}
                  questionnaire on{" "}
                  <span className="font-semibold text-gray-900">
                    {existingResponseData?.created_at
                      ? new Date(
                        existingResponseData.created_at,
                      ).toLocaleDateString("en-US", {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })
                      : ""}
                  </span>
                  .
                </p>
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <p className="text-sm text-amber-800">
                    <strong>Previous Score:</strong>{" "}
                    {existingResponseData?.total_score}
                  </p>
                  <p className="text-sm text-amber-800">
                    <strong>Status:</strong>{" "}
                    <span className="capitalize">
                      {existingResponseData?.status?.replace(/_/g, " ")}
                    </span>
                  </p>
                </div>
                <p className="text-sm text-gray-600">
                  Would you like to take the survey again? Your previous
                  response will be kept for records.
                </p>
              </div>
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-3 pt-4">
            <Button
              variant="outline"
              onClick={() => {
                setShowRedoDialog(false);
                toast.info("Thank you! Your previous response is on record.");
                setCurrentStep(4); // Skip to completion step
              }}
            >
              No, I'm Done
            </Button>
            <Button
              onClick={() => {
                setShowRedoDialog(false);
                // Continue with survey flow normally
              }}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              Yes, Retake Survey
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <div className="container mx-auto px-3 sm:px-4 py-4 sm:py-6">
        {/* Patient Information Header */}
        <Card className="mb-4 sm:mb-6 bg-white shadow-sm border border-gray-200 rounded-xl sm:rounded-2xl overflow-hidden">
          <CardContent className="p-3 sm:p-4 md:p-6">
            <div className="flex flex-col gap-4">
              {/* Patient Info Row */}
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex items-start sm:items-center gap-3">
                  <div className="w-14 h-14 sm:w-16 sm:h-16 bg-green-500 rounded-full flex-shrink-0 flex items-center justify-center text-white font-bold text-lg sm:text-xl shadow-lg">
                    {patient.first_name?.[0]}
                    {patient.last_name?.[0]}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h1 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-800 truncate">
                      {patient.first_name} {patient.last_name}
                    </h1>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs sm:text-sm text-gray-600 mt-1">
                      {patient.mobile_number && (
                        <span className="flex items-center gap-1 whitespace-nowrap">
                          <Phone className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
                          <span className="truncate max-w-[120px]">
                            {formatPhoneDisplay(patient.mobile_number)}
                          </span>
                        </span>
                      )}
                      {patient.date_of_birth && (
                        <span className="flex items-center gap-1 whitespace-nowrap">
                          <Calendar className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
                          <span className="truncate">
                            {new Date(
                              patient.date_of_birth,
                            ).toLocaleDateString()}
                          </span>
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="text-right sm:text-left sm:pl-4 border-t pt-2 sm:border-t-0 sm:pt-0">
                  <div className="text-xs sm:text-sm text-gray-500">Gender</div>
                  <div className="font-medium text-sm sm:text-base text-gray-800 capitalize">
                    {patient.gender || "Not specified"}
                  </div>
                </div>
              </div>

              {/* Provider & Support Info Row */}
              <div className="border-t border-gray-100 pt-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {/* Primary Provider */}
                  {primaryProvider && (
                    <div className="flex items-center gap-3 bg-blue-50 rounded-lg p-3">
                      <div className="w-10 h-10 bg-blue-500 rounded-full flex-shrink-0 flex items-center justify-center text-white">
                        <Stethoscope className="w-5 h-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs text-blue-600 font-medium">
                          Your Doctor
                        </div>
                        <div className="text-sm font-semibold text-gray-800 truncate">
                          {primaryProvider.name}
                        </div>
                        <div className="text-xs text-gray-500 truncate">
                          {primaryProvider.email}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Support Contact */}
                  <div className="flex items-center gap-3 bg-purple-50 rounded-lg p-3">
                    <div className="w-10 h-10 bg-purple-500 rounded-full flex-shrink-0 flex items-center justify-center text-white">
                      <Headphones className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs text-purple-600 font-medium">
                        Support Contact
                      </div>
                      <div className="text-sm font-semibold text-gray-800 truncate">
                        {supportContact.name}
                      </div>
                      <div className="text-xs text-gray-500 truncate">
                        {supportContact.email}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Stepper */}
        <div className="max-w-4xl mx-auto mb-4 sm:mb-6 px-2">
          <Stepper
            currentStep={currentStep}
            totalSteps={steps.length}
            steps={steps}
          />
        </div>

        {/* Step Content */}
        <div className="max-w-4xl mx-auto px-2 sm:px-0">
          {currentStep === 1 && (
            <HIPAAConsentStep
              patientName={patient.first_name + " " + patient.last_name}
              patientId={patientId!}
              onNext={handleNext}
            />
          )}

          {currentStep === 2 && (
            <BiometricVerificationStep
              patientId={patientId!}
              onNext={handleNext}
              onBack={handleBack}
            />
          )}

          {currentStep === 3 && (
            <QuestionnaireStep
              patientId={patientId!}
              onComplete={handleQuestionnaireComplete}
            />
          )}

          {currentStep === 4 && (
            <div className="text-center py-8 sm:py-12 px-2">
              <div className="bg-green-50 w-14 h-14 sm:w-16 sm:h-16 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6 border-2 border-green-200">
                <Check className="w-7 h-7 sm:w-8 sm:h-8 text-green-600" />
              </div>
              <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-2">
                Questionnaires Completed!
              </h2>
              <p className="text-gray-600 mb-6 text-sm sm:text-base">
                {(() => {
                  const tests = Array.isArray(patient?.selected_tests) && patient.selected_tests.length > 0
                    ? patient.selected_tests
                    : null;
                  if (!tests) return "Thank you for completing the questionnaire.";
                  if (tests.length === 1) return `Thank you for completing the ${tests[0]} questionnaire.`;
                  const last = tests[tests.length - 1];
                  const rest = tests.slice(0, -1).join(", ");
                  return `Thank you for completing the ${rest} and ${last} questionnaires.`;
                })()}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
